import { gql } from '@apollo/client'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import type { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import { BreadcrumbJsonLd, NextSeo, ProductJsonLd } from 'next-seo'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import Script from 'next/script'
import { useEffect } from 'react'
import getApolloClient, { FAKE_CTX } from 'src/apollo-client'
import type { UnavailableReason } from 'src/components/Details/UnavailableBanner'
import { Loader } from 'src/components/Loaders/Loader'
import { LOC_CONFIGURATION_FRAGMENT } from 'src/constants/fragments'
import { getBreadcrumbs } from 'src/constants/fuse'
import {
  HOOD_MAP,
  PILLAR_PAGE_MAP,
  PillarPage,
  SEARCH_PAGE_CITY_MAP,
  SearchHood,
  SearchPageCity
} from 'src/constants/searchPages'
import useCommon from 'src/hooks/useCommon'
import { useTrackLastSearch } from 'src/hooks/useLastSearch'
import { useGetPrice } from 'src/hooks/usePrice'
import {
  ICoordinates,
  ILocation,
  Interest,
  IQuery,
  IQueryLocationByConfigurationArgs,
  IQuerySearchLocationsArgs,
  IUserReviewsArgs
} from 'src/types/graphql'
import { RedirectUrls } from 'src/types/types'
import { numberWithCommas } from 'src/utils'
import { ErrorCode } from 'src/utils/errors'
import { loadTranslations } from 'src/utils/i18nServerProps'
import { markListingOutOfStock } from 'src/utils/pixels/spotmeEvents'
import { safeJsonLdStringify } from 'src/utils/safeJsonLdStringify'
import { toOgLocale } from 'src/utils/seo'
import { isValidUUID } from 'src/utils/validateId'

import { getLocationProfile } from 'src/utils/profile'

import { DEFAULT_STRUCTURED_DATA } from 'src/constants/structuredData'
import {
  listingDisclosesHost,
  scrubHostIdentity
} from 'src/lib/listingVariants'

import Details from 'src/components/Details'
import MobileNavbar from 'src/components/Details/MobileNavbar'
import Footer from 'src/components/Footer'
import LandingNav from 'src/components/LandingNav'
const GooglePopup = dynamic(() => import('src/components/Login/GooglePopup'), {
  ssr: false
})

const GET_LOCATIONS = gql`
  query SearchLocations(
    $pagination: PaginationInput!
    $searchInput: SearchLocationsInput!
  ) {
    searchLocations(pagination: $pagination, searchInput: $searchInput) {
      total
      hits {
        id
        addressDetails {
          neighborhood
          colonia
          city
        }
        configurations {
          id
          pillarPage
        }
      }
    }
  }
`

// Public listing fragment — only fields rendered by `Details` (and its
// children: User, Reviews, PriceCard, Header, SizeSection, Reports, etc.) and
// SEO/JSON-LD on this page. Deliberately excludes:
//   - Host PII (rfc, curp, clabe, identification, address, lastKnownLocation,
//     birthdate, phoneNumber, email, fiscalCompliance, regimenFiscal, usoCFDI,
//     civilStatus, customerType, hostType, constanciaFiscal, adminRole,
//     accountVerificationError) — these were leaking into __NEXT_DATA__ on a
//     publicly-served HTML page via `...CoreUserFields`.
//   - Reviewer PII (same shape, multiplied per review). The "all reviews"
//     modal (lazy-loaded) re-fetches reviewers via its own GET_USER query.
//   - Photo `id` and `name` fields — public rendering only consumes `url`,
//     `width`, `height`. The admin edit flow (AdminEdit) does its own top-up
//     query (GET_LOCATION_ADMIN) for full photo metadata + admin fields, so
//     editing/saving round-trips safely without bloating SSR.
// Result: ~50% smaller `__NEXT_DATA__` and removes the public PII surface.
//
// `submittedBy` IS kept because PriceCard uses it on the non-admin path to
// detect "is the current user the listing owner?" and disable the Reserve
// button accordingly.
//
// When adding an admin-only field, put it in `GET_LOCATION_ADMIN` inside
// `src/components/Details/AdminEdit.tsx`, NOT here.
const PUBLIC_LISTING_FIELDS = gql`
  ${LOC_CONFIGURATION_FRAGMENT}

  fragment PublicListingFields on Location {
    id
    urlRoute
    address
    shortAddress
    addressDetails {
      street1
      street2
      neighborhood
      colonia
      city
      state
      zipcode
      country
    }
    coordinates {
      lat
      lng
    }
    publicAddress

    title
    description
    privateSpace
    indoorOrOutdoor
    currency
    currentCurrency
    # Operator confirmed its own price includes IVA → PriceCard says
    # "IVA incluido" instead of "+ IVA = $X" (Traditional only).
    listPriceIncludesIva
    photos {
      url
      width
      height
    }
    coverPhoto {
      url
      width
      height
    }
    welcomeMessage
    welcomePhotos {
      url
      width
      height
    }
    accessPermission
    accessFrequency
    accessHours
    accessAvailability
    useOfSpace
    propertyType
    logisticsServices

    defaultConfigurationId
    defaultConfiguration {
      ...LocConfigurationFields
      operatorPromo {
        kind
        headline
        conditions
        promoPrice
        percentOff
        isUpTo
        freeMonths
        tiers {
          minMonths
          percentOff
        }
      }
    }
    configurations {
      ...LocConfigurationFields
      # Read-only, server-gated operator promotion — deliberately NOT in
      # LocConfigurationFields, which edit flows spread into ConfigurationInput.
      operatorPromo {
        kind
        headline
        conditions
        promoPrice
        percentOff
        isUpTo
        freeMonths
        tiers {
          minMonths
          percentOff
        }
      }
    }

    submittedBy
    submittedOn
    lastUpdatedOn

    isDeactivated
    isDraft
    isDeleted
    isVerified

    visualScore
    reviewScore
    reviewCount

    hasTechnicalReport

    # Drives the anonymous host block's "Anfitrión verificado" heading and its
    # verification rows. NOT user.isVerified: that resolves off submittedBy,
    # which on an admin-onboarded team listing is a SpotMe admin, so the badge
    # would be describing us. Carries no identity, so it survives
    # scrubHostIdentity.
    hostIsVerified

    user {
      id
      firstname
      lastname
      profilePicture {
        url
      }
      description
      isVerified
      createdOn
      bussinessProfile {
        name
        description
        profilePicture {
          url
        }
      }
    }
    bussinessProfile {
      name
      description
      profilePicture {
        url
      }
    }
    teamId
    # Team-owned listings are the TEAM's, so the host masthead must name the
    # operator, not whichever member (usually a SpotMe admin) typed the listing
    # in. Stripped from the props in getStaticProps for every variant that does
    # NOT disclose its landlord — see listingDisclosesHost.
    team {
      id
      name
      description
      icon {
        url
      }
    }
  }
`

const GET_CONFIGURATION = gql`
  ${PUBLIC_LISTING_FIELDS}
  query GetLocationStatic(
    $configurationId: String!
    $pagination: PaginationInput!
    $interest: Interest!
  ) {
    locationByConfiguration(configurationId: $configurationId) {
      canonicalUrl

      ...PublicListingFields
      user {
        reviews(pagination: $pagination, interest: $interest) {
          total
          avgRating
          hits {
            rating
            review
            createdAt
            response
            respondedAt
            reviewer {
              id
              firstname
              lastname
              profilePicture {
                url
              }
            }
          }
        }
      }

      defaultConfiguration {
        watchingCount
      }
      configurations {
        watchingCount
      }
    }
  }
`

const truncateText = (
  text: string | null | undefined,
  maxLength: number
): string => {
  if (!text) return ''
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
}

const { titleF, descriptionF } = {
  titleF: (location: ILocation, locType: string, sizeText: string) => {
    const { shortAddress } = location
    const truncatedAddress = truncateText(shortAddress, 25)
    return `${locType} ${sizeText} | ${truncatedAddress} | SpotMe`
  },
  descriptionF: (
    location: ILocation,
    locType: string,
    sizeText: string,
    pillar: PillarPage,
    price?: number
  ) => {
    const { shortAddress } = location
    const truncatedAddress = truncateText(shortAddress, 30)
    const { searchName } = PILLAR_PAGE_MAP[pillar] ?? {}
    const priceText =
      price && price > 0
        ? ` Desde $${numberWithCommas(Math.round(price))}/mes.`
        : ''
    let description = `✓ ${locType} ${sizeText} en ${truncatedAddress}.${priceText}`

    if (description.length < 130) {
      description = `${description} ${searchName} en SpotMe.`
    }

    if (description.length < 130) {
      description = `${description} Espacios verificados, sin contratos largos.`
    }

    return description
  }
}

export type IProps = {
  location: ILocation
  resolvedUrl: string
  locale: string
  configurationId: string
  breadcrumbs: [SearchPageCity | null, SearchHood | null]
  priceValidUntil: string
  /**
   * When set, the listing is rendered in an "unavailable" state via
   * <UnavailableBanner> + replaced PriceCard CTAs. The URL stays stable
   * (Inmuebles24/Airbnb pattern) instead of redirecting to search — see
   * root CLAUDE.md → "Unavailable listings" rule.
   */
  unavailableReason?: UnavailableReason | null
  /**
   * Does this listing name its landlord to renters? Computed in
   * `getStaticProps` from `listingDisclosesHost` — the SAME verdict that
   * decides whether `location.team` survives into the props — so the meta
   * tags can never name a host the page itself withholds.
   */
  disclosesHost: boolean
}

const Page: NextPage<IProps> = ({
  location,
  resolvedUrl,
  locale,
  configurationId,
  breadcrumbs,
  priceValidUntil,
  unavailableReason,
  disclosesHost
}) => {
  const { locMap, showSize } = useCommon()
  const { getPrice } = useGetPrice()

  const router = useRouter()

  // Persist this listing visit as the renter's last search so the home-page
  // resume card sends them back to the same city/hood next session. The
  // listing itself isn't saved (it may be unavailable later) — the rail at
  // city/hood level always has content. Called before the early return to
  // satisfy the rules of hooks.
  useTrackLastSearch({
    pillar:
      (location?.defaultConfiguration?.pillarPage as PillarPage | undefined) ??
      null,
    city: breadcrumbs?.[0],
    hood: breadcrumbs?.[1]
  })

  // Fire Meta `ViewContent` with availability='out of stock' once per mount
  // when the listing is unavailable. Closes the DPA loop without needing the
  // old <LostListingToast> on the search page — Meta's catalog refresher
  // picks up the signal within ~24h and stops serving the dead creative.
  useEffect(() => {
    if (!unavailableReason || !configurationId) return
    markListingOutOfStock(configurationId)
  }, [unavailableReason, configurationId])

  if (router.isFallback) return <Loader />

  // `getStaticProps` returns `IProps | {}` — its redirect branches
  // (buildLostListingRedirect / Home redirect for lost/deleted listings) ship
  // `props: {}`. On a client-side transition INTO such a listing (e.g. tapping a
  // search-result card whose listing has since been deleted), Next renders this
  // component once with empty pageProps before the `__N_REDIRECT` completes, so
  // `location` is briefly undefined. Guard before destructuring it below —
  // otherwise `Cannot destructure property 'reviewScore' from undefined` throws
  // (seen in Datadog from 2026-06-30). The redirect finishes right after.
  if (!location) return <Loader />

  const locType = locMap(location)
  const sizeText = showSize(location, true)

  const { coverPhoto, photos, addressDetails, defaultConfiguration } = location
  const image = coverPhoto?.url ?? 'https://www.spotme.mx/images/cover.png'
  const canonicalUrl = `https://www.spotme.mx${location.canonicalUrl}`
  const url = `https://www.spotme.mx${resolvedUrl}`

  const coverPhotoUrl = coverPhoto?.url
  const photosUrls = photos
    ?.map(({ url }) => url)
    ?.filter((url) => !!url) as string[]
  const images = coverPhotoUrl
    ? [coverPhotoUrl, ...(photosUrls ?? [])]
    : photosUrls
  // Host identity for the product meta tags. When the listing withholds its
  // landlord, `location.team` has already been stripped from the props, so
  // `getLocationProfile` would fall back to the INDIVIDUAL submitter — a real
  // person's name and photo in `product:brand` / `manufacturerName`, which is
  // a worse leak than the team name we just removed. Fall back to the SpotMe
  // brand instead; `brand` on the JSON-LD below is already hardcoded to it.
  const hostProfile = getLocationProfile(location, configurationId)
  const brandName = disclosesHost ? (hostProfile.name ?? 'SpotMe') : 'SpotMe'
  const brandLogo = disclosesHost
    ? (hostProfile.pp?.url ?? '')
    : DEFAULT_STRUCTURED_DATA.logo

  const [foundCity, foundHood] = breadcrumbs
  const { city, colonia } = addressDetails ?? {}
  const { pillarPage } = defaultConfiguration ?? {}
  const { label, keywords } = PILLAR_PAGE_MAP[pillarPage as PillarPage] ?? {}
  const breadcrumbList = [
    {
      id: 'Rent',
      name: 'Rent',
      item: 'https://www.spotme.mx'
    },
    {
      id: pillarPage,
      name: label,
      item: `https://www.spotme.mx/${pillarPage}`
    },
    {
      id: foundCity,
      name: city,
      item: `https://www.spotme.mx/${pillarPage}/${foundCity}`
    },
    {
      id: foundHood,
      name: colonia,
      item: `https://www.spotme.mx/${pillarPage}/${foundCity}/${foundHood}`
    }
  ].filter(({ id }) => !!id)

  const fprice = getPrice(location, defaultConfiguration)

  const title = titleF(location, locType, sizeText)
  const description = descriptionF(
    location,
    locType,
    sizeText,
    pillarPage as PillarPage,
    fprice
  )
  const discountMetaTags = defaultConfiguration?.discountEnabled
    ? [
        {
          property: 'product:sale_price:amount',
          content: (fprice * 0.5).toString()
        },
        {
          property: 'product:sale_price:currency',
          content: 'MXN'
        }
      ]
    : []

  const { coordinates } = location
  const placeJsonLd = coordinates
    ? {
        '@context': 'https://schema.org',
        '@type': 'Place',
        name: `${locType} ${sizeText} en ${addressDetails?.city ?? 'México'}`,
        address: {
          '@type': 'PostalAddress',
          addressLocality: addressDetails?.colonia ?? addressDetails?.city,
          addressRegion: addressDetails?.state,
          addressCountry: 'MX'
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: coordinates.lat,
          longitude: coordinates.lng
        }
        // No aggregateRating/review markup (2026-09-30): `reviewScore` is
        // hand-set, not derived from real reviews. Rating markup must only
        // ever come from genuine stored reviews.
      }
    : null

  return (
    <>
      <NextSeo
        noindex
        title={title}
        description={description}
        canonical={canonicalUrl}
        openGraph={{
          type: 'product',
          url,
          title,
          description,
          locale: toOgLocale(locale),
          images: [
            { url: image, width: 1200, height: 630, alt: title },
            ...(photosUrls
              ?.filter((u) => u !== coverPhoto?.url)
              ?.slice(0, 5)
              ?.map((photoUrl) => ({
                url: photoUrl,
                width: 1200,
                height: 630,
                alt: `${locType} ${sizeText} - foto`
              })) ?? [])
          ]
        }}
        additionalMetaTags={[
          { name: 'keywords', content: keywords.join(', ') },
          { property: 'product:brand', content: brandName },
          {
            property: 'product:availability',
            content: unavailableReason ? 'out of stock' : 'in stock'
          },
          { property: 'product:condition', content: 'new' },
          {
            property: 'product:price:amount',
            content: fprice.toString() ?? ''
          },
          ...discountMetaTags,
          { property: 'product:price:currency', content: 'MXN' },
          {
            property: 'product:retailer_item_id',
            content: configurationId
          },
          { property: 'product:item_group_id', content: location.id },
          { property: 'product:category', content: '5830' },
          {
            property: 'product:product_type',
            content: locType
          },
          {
            property: 'product:description',
            content: location.description ?? description
          },
          ...(coordinates
            ? [
                {
                  property: 'place:location:latitude',
                  content: coordinates.lat.toString()
                },
                {
                  property: 'place:location:longitude',
                  content: coordinates.lng.toString()
                }
              ]
            : [])
        ]}
      />
      <ProductJsonLd
        productName={title.slice(0, 65)}
        images={images}
        description={location.description ?? description}
        brand="SpotMe"
        sku={configurationId}
        manufacturerName={brandName}
        manufacturerLogo={brandLogo}
        releaseDate={location.submittedOn ?? ''}
        category={'5830'}
        offers={[
          {
            price: fprice?.toString() ?? '',
            priceCurrency: 'MXN',
            priceValidUntil,
            itemCondition: 'https://schema.org/NewCondition',
            availability: unavailableReason
              ? 'https://schema.org/OutOfStock'
              : 'https://schema.org/InStock',
            url:
              'https://www.spotme.mx' + (defaultConfiguration?.urlRoute ?? ''),
            // No hasMerchantReturnPolicy: this is a rental, not a retail
            // return. Refund terms live in docs/refund-policy.md (legal-sync).
            // `brandName`, NOT a bare `name` — that resolved to the DOM's
            // global `name` (so tsc stayed silent) and, once host props were
            // scrubbed, would have leaked the individual submitter here.
            seller: { name: brandName }
          }
        ]}
        productID={configurationId}
        additionalProperty={[
          {
            '@type': 'PropertyValue',
            propertyID: 'item_group_id',
            value: location.id
          }
        ]}
      />
      {placeJsonLd && (
        <Script
          id="place-jsonld"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: safeJsonLdStringify(placeJsonLd) }}
        />
      )}
      <BreadcrumbJsonLd
        itemListElements={breadcrumbList.map(({ name, item }, index) => ({
          position: index + 1,
          name,
          item
        }))}
      />

      <main>
        <div className="md:hidden">
          <MobileNavbar />
        </div>
        <div className="hidden md:block">
          <LandingNav showSearch />
        </div>

        <Details
          location={location}
          configurationId={configurationId}
          breadcrumbs={breadcrumbs}
          unavailableReason={unavailableReason ?? null}
        />

        <div className="hidden md:block">
          <Footer />
        </div>

        <GooglePopup />
      </main>
    </>
  )
}

export default Page

type IParams = {
  pillar: PillarPage
  city: string
  hood: string
  configId: string
}

const PLUS_CODE_PREFIX_RE = /^[A-Z0-9]{4,}\+[A-Z0-9]*[\s,]*/i

const stripPlusCode = (value: string): string | null => {
  const trimmed = value.trim()
  if (!PLUS_CODE_PREFIX_RE.test(trimmed)) return trimmed
  const rest = trimmed.replace(PLUS_CODE_PREFIX_RE, '').trim()
  return rest.length > 2 ? rest : null
}

const slugify = (str: string): string =>
  str
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replaceAll(/[,;:()'"!?#@&={}[\]|\\<>]/g, '')
    .replaceAll(/[+]/g, '-')
    .replaceAll(/[\s_.]+/g, '-')
    .replaceAll(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')

export const getStaticPaths = (async ({ locales = [] }) => {
  const paths: { params: IParams; locale: string }[] = []
  const client = getApolloClient(FAKE_CTX)

  const { lat, lng } = SEARCH_PAGE_CITY_MAP[SearchPageCity.Monterrey]
    .center as ICoordinates
  // Pre-render EVERY active listing, not just the 800 nearest Monterrey.
  // The backend search is a bounding box (center + zoom); zoom:1 clamps to
  // minZoom 3.1, whose box spans ~±75° lng — it covers all of Mexico. So the
  // ONLY real limit was the 800 pageSize cap, which truncated the long tail:
  // far-from-Monterrey listings ranked past #800 and fell back to on-demand
  // `blocking` regeneration. Organic Google traffic lands on that cold tail
  // (~59% poor LCP — the worst listing cohort), so we paginate until exhausted
  // and build them all at deploy time (durably warm, no first-visit blocking).
  // Loop breaks early once the catalog is exhausted; the MAX_PAGES cap bounds
  // build time and keeps from+pageSize under ES's 10k result window. If the
  // catalog ever outgrows the cap, switch to a popularity-ranked subset + a
  // warm cron rather than raising it past the ES window.
  const PAGE_SIZE = 500
  const MAX_PAGES = 10
  const seenLocationIds = new Set<string>()
  const locations: NonNullable<NonNullable<IQuery['searchLocations']>['hits']> =
    []
  for (let page = 0; page < MAX_PAGES; page++) {
    const response = await client.query<
      Pick<IQuery, 'searchLocations'>,
      IQuerySearchLocationsArgs
    >({
      query: GET_LOCATIONS,
      variables: {
        pagination: { from: page * PAGE_SIZE, pageSize: PAGE_SIZE },
        searchInput: { center: { lat, lng }, zoom: 1 }
      }
    })
    const hits = response.data?.searchLocations?.hits ?? []
    for (const hit of hits) {
      if (hit?.id && !seenLocationIds.has(hit.id)) {
        seenLocationIds.add(hit.id)
        locations.push(hit)
      }
    }
    if (hits.length < PAGE_SIZE) break
  }
  for (const locale of locales) {
    for (const location of locations) {
      const { configurations, addressDetails } = location
      const { state, city, colonia, neighborhood } = addressDetails ?? {}
      const configs =
        configurations?.filter(
          (i) => !i.isDeactivated && !i.isDeleted && (i.availability ?? 0) > 0
        ) ?? []
      for (const config of configs) {
        const { id, pillarPage } = config
        const clean = (v: string | undefined | null) =>
          v ? stripPlusCode(v) : null
        const hood = clean(neighborhood) ?? clean(colonia) ?? city ?? state
        if (config.isDeactivated || config.isDeleted) {
          continue
        }
        paths.push({
          params: {
            pillar: pillarPage as PillarPage,
            city: slugify(city ?? 'mexico'),
            hood: slugify(hood ?? 'mexico'),
            configId: id
          },
          locale
        })
      }
    }
  }

  return { paths, fallback: 'blocking' }
}) satisfies GetStaticPaths

/**
 * Stale-listing salvage redirect target.
 *
 * When the configurationId is valid UUID-shape but the backend says the
 * listing doesn't exist / is deactivated / soft-deleted, redirect to the
 * pillar+city+hood search page instead of 404. The destination mounts
 * `<LostListingToast/>` which (a) shows a toast and (b) fires Meta
 * `ViewContent` with `availability: 'out of stock'` so the DPA catalog
 * eventually drops the dead creative.
 *
 * `lostId` is the `configurationId` (UUID). Live listings emit
 * `<meta property="product:retailer_item_id" content="{configurationId}">`,
 * which Meta's "Items from Website" catalog uses as the canonical item key.
 * Passing the same UUID as the pixel `content_ids` value closes the loop —
 * Meta matches the out-of-stock signal to the right catalog entry regardless
 * of any URL changes (hood rename, canonical-URL drift, etc.).
 */
const buildLostListingRedirect = (params: IParams) => {
  const { pillar, city, hood, configId } = params
  // The listing's `[hood]` slug can be any string the backend put into the
  // urlRoute, but the search page at /[pillar]/[city]/[hood] 404s when the
  // hood isn't in HOOD_MAP. Only include the hood in the redirect when it's
  // a valid HOOD_MAP key — otherwise fall back to /[pillar]/[city], which
  // is always a valid search landing.
  const hoodSegment = hood && hood in HOOD_MAP ? `/${hood}` : ''
  // Same trap one segment up, and it bit on 2026-08-30: the `[city]` slug is
  // whatever the backend baked into urlRoute, and `/[pillar]/[city]` 404s on
  // exactly the same test the hood page uses (it re-exports that page's
  // getServerSideProps). A listing whose address carried the formal
  // municipality name — `leon-de-los-aldama` for León — salvaged straight into
  // a 404, so the renter got a dead end AND Meta never learned the item was
  // out of stock.
  //
  // The fallback is `/search`, NOT the pillar root: `/[pillar]` is a marketing
  // landing page and does not mount <LostListingToast/>, so redirecting there
  // would silently drop both the toast and the DPA out-of-stock ping — the two
  // things this redirect exists for. `/search` mounts it, and it is already
  // the lost-listing target used by `pages/c/[nanoId].tsx`.
  const isCityPage = (Object.values(SearchPageCity) as string[]).includes(city)
  const base = isCityPage
    ? `/${pillar}/${city}${hoodSegment}`
    : RedirectUrls.Search
  const destination = `${base}?lost=1&lostId=${encodeURIComponent(configId)}`
  return {
    redirect: { destination, permanent: false },
    revalidate: LISTING_REVALIDATE_SECONDS
  } as const
}

/**
 * ISR window for EVERY `getStaticProps` outcome on this page — props AND the
 * redirect branches. A `redirect` returned without `revalidate` is cached
 * with `revalidate: false`, i.e. until the next deploy (Next `render.js`:
 * `typeof data.revalidate === 'undefined'` → `revalidate = false`). On
 * 2026-08-27 the backend's post-approval on-demand revalidate raced the ES
 * refresh, `locationByConfiguration` said "not found" for one second, and
 * the lost-listing 307 was pinned on a LIVE listing with no way to recover
 * but another manual revalidate. A transient miss must self-heal.
 */
const LISTING_REVALIDATE_SECONDS = 60

export const getStaticProps: GetStaticProps<IProps | {}> = async (context) => {
  const { locale, params } = context
  const typedParams = params as IParams
  const { configId } = typedParams
  const configurationId =
    configId && typeof configId === 'string' ? configId : null

  // Malformed routing artifact — genuine bad URL, not a deactivation. 404.
  if (!configurationId || configurationId === '[configId]') {
    return { notFound: true }
  }

  // Not-a-UUID — genuine bad input (e.g. scraper guessing IDs). 404.
  if (!isValidUUID(configurationId)) {
    return { notFound: true }
  }

  const client = getApolloClient(FAKE_CTX)
  const response = await client.query<
    Pick<IQuery, 'locationByConfiguration'>,
    IQueryLocationByConfigurationArgs & IUserReviewsArgs
  >({
    query: GET_CONFIGURATION,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    variables: {
      configurationId,
      pagination: { from: 0, pageSize: 4 },
      interest: Interest.Host
    }
  })

  if (response.error && !response.data?.locationByConfiguration) {
    const gqlErrors = CombinedGraphQLErrors.is(response.error)
      ? response.error.errors
      : []
    const code = gqlErrors[0]?.extensions?.code as ErrorCode | undefined
    const isExpected =
      code === ErrorCode.BadUserInput ||
      code === ErrorCode.Unauthenticated ||
      code === ErrorCode.Forbidden
    if (isExpected) {
      // Listing doesn't exist anymore — salvage Meta DPA / cached-link traffic.
      return buildLostListingRedirect(typedParams)
    }
    return {
      props: {},
      redirect: {
        destination: RedirectUrls.Home,
        permanent: false
      },
      revalidate: LISTING_REVALIDATE_SECONDS
    }
  }

  const translations = await loadTranslations(locale, [
    'footer',
    'nav',
    'platform_stats',
    'common',
    'details',
    // `size_guide` intentionally NOT shipped here — it's ~12 kB of size
    // categories consumed only by the interaction-gated StorageSizeModal. The
    // one first-paint key (CTA_QUESTION, the size-guide CTA prompt) now lives in
    // `details`; the heavy namespace is lazy-loaded via `useLazyNamespace`
    // in Details/index.tsx when the modal opens. See docs/performance/large-page-data.md.
    // `host_listing` intentionally NOT shipped here — it's admin-only (consumed
    // solely by AdminEdit). It's lazy-loaded client-side via `useLazyNamespace`
    // when the admin panel mounts, keeping this page's __NEXT_DATA__ ~13.6 kB
    // lighter for the 99.9% renter case. See docs/performance/large-page-data.md.
    'auth'
  ])

  const location = response.data?.locationByConfiguration
  // Lookup succeeded but no location returned — nothing to render. Keep
  // the lost-listing redirect for this case only (data-less): the renter
  // gets the LostListingToast + DPA out-of-stock fire from the destination.
  if (!location) return buildLostListingRedirect(typedParams)

  // Pick the configuration the URL targets, falling back to defaultConfiguration.
  // The server opts SSG calls into the `includeUnavailable` ES path via the
  // `client-secret` → `ctx.isServer` plumbing in `getContext`, so paused
  // (isDeactivated) configs reach this code. Truly-deleted configs are
  // hard-filtered at the ES layer and never get here — they fall into the
  // GraphQL-error branch above which redirects to search with the legacy
  // LostListingToast (intentional: deleted = junk admin cleanup, not legit
  // inventory worth a banner).
  const targetConfig =
    location.configurations?.find((c) => c.id === configurationId) ??
    location.defaultConfiguration

  const isDeactivated = !!targetConfig?.isDeactivated
  const isSoldOut = !isDeactivated && (targetConfig?.availability ?? 0) <= 0

  const unavailableReason: UnavailableReason | null = isDeactivated
    ? 'paused'
    : isSoldOut
      ? 'sold_out'
      : null

  // Host anonymity: Broker / Traditional / Fleet listings replace or hide the
  // host section precisely so a renter can't route around SpotMe to the
  // operator. Shipping `team` in the props would defeat that even though the
  // section never renders — the name would sit in `__NEXT_DATA__` and flow
  // into `product:brand` + the JSON-LD `manufacturerName` via
  // `getLocationProfile`. Keep `teamId` (opaque, already public) and drop the
  // identity. Monthly listings, which DO show a host, keep it.
  // Hiding the host SECTION is not enough — whatever is returned here is
  // serialised into `__NEXT_DATA__` on a public HTML page, so an unrendered
  // name is one "view source" away. `scrubHostIdentity` drops all three
  // carriers (team, bussinessProfile, and the individual user's name/photo).
  const disclosesHost = listingDisclosesHost(location as ILocation)
  const publicLocation = disclosesHost
    ? location
    : scrubHostIdentity(location as ILocation)

  return {
    props: {
      ...translations,
      location: publicLocation,
      resolvedUrl: location.urlRoute,
      locale,
      configurationId,
      breadcrumbs: getBreadcrumbs(location as ILocation),
      // Computed at build time so SSR HTML matches client hydration.
      // Doing this in render uses Date.now() which differs between
      // server and client, causing React error #418.
      priceValidUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0],
      unavailableReason,
      disclosesHost
    },
    revalidate: LISTING_REVALIDATE_SECONDS
  }
}
